import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiMode';
import { useApp } from '../context/AppContext';
import { Mail, Copy, Check, RefreshCw, User, Building2, Calendar, Loader, ExternalLink } from 'lucide-react';

const API = `${API_BASE_URL}/api/admin`;

const SCENARIO_LABELS = {
  middle_mile: 'Middle Mile',
  solar_panel: 'Solar Panel',
  single_route: 'Single Route',
};

const TAG_COLORS = {
  middle_mile:  { bg: '#EBF5FF', color: '#1D4ED8' },
  solar_panel:  { bg: '#FEF9C3', color: '#854D0E' },
  single_route: { bg: '#F0FDF4', color: '#166534' },
};

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('it-IT', { day:'2-digit', month:'short', year:'numeric' }) : '—';
}

function ScenarioBadge({ type }) {
  const tag = TAG_COLORS[type] || { bg:'#F5F5F5', color:'#555' };
  return (
    <span style={{
      background: tag.bg, color: tag.color,
      borderRadius:5, padding:'3px 8px', fontSize:11, fontWeight:700,
      textTransform:'uppercase', letterSpacing:'.3px', whiteSpace:'nowrap'
    }}>
      {SCENARIO_LABELS[type] || type}
    </span>
  );
}

function LeadCard({ lead, onDone, token }) {
  const [draft, setDraft]           = useState(null);
  const [subject, setSubject]       = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError]     = useState(null);
  const [copied, setCopied]         = useState(false);
  const [marking, setMarking]       = useState(false);
  const [sendError, setSendError]   = useState(null);
  const [translating, setTranslating] = useState(false);
  const [currentLang, setCurrentLang] = useState(null); // 'en' | 'it'

  const generateEmail = useCallback(async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const { data } = await axios.post(
        `${API}/followups/user/${lead.user_id}/email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDraft(data.email);
      if (data.subject) setSubject(data.subject);
    } catch (e) {
      setGenError(e.response?.data?.error || e.message || 'Errore generazione email');
    } finally {
      setGenerating(false);
    }
  }, [lead.user_id, token]);

  // Se esiste già un draft salvato, usalo senza chiamare Claude
  useEffect(() => {
    if (lead.followup_draft) {
      setDraft(lead.followup_draft);
      setSubject(lead.followup_subject || '');
    } else {
      generateEmail();
    }
  }, []);

  const handleCopy = () => {
    if (!draft) return;
    const full = subject ? `Oggetto: ${subject}\n\n${draft}` : draft;
    navigator.clipboard.writeText(full).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      const el = document.getElementById(`ta-${lead.user_id}`);
      if (el) { el.select(); document.execCommand('copy'); }
    });
  };

  const handleTranslate = async (targetLang) => {
    setTranslating(true);
    setGenError(null);
    try {
      const { data } = await axios.post(
        `${API}/followups/user/${lead.user_id}/translate`,
        { draft, subject, targetLang },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDraft(data.email);
      if (data.subject) setSubject(data.subject);
      setCurrentLang(targetLang);
    } catch (e) {
      setGenError(e.response?.data?.error || e.message || 'Errore traduzione');
    } finally {
      setTranslating(false);
    }
  };

  const handleOpenOutlook = () => {
    const mailtoUrl = `mailto:${encodeURIComponent(lead.email)}?subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(draft || '')}`;
    window.location.href = mailtoUrl;
  };

  const handleDone = async () => {
    setMarking(true);
    setSendError(null);
    try {
      await axios.patch(
        `${API}/leads/user/${lead.user_id}/followup`,
        { draft, subject },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onDone(lead.user_id);
    } catch (e) {
      setSendError('Errore invio: ' + (e.response?.data?.error || e.message));
      setMarking(false);
    }
  };

  return (
    <div style={{
      background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.08)',
      marginBottom:20, overflow:'hidden', border:'1px solid #F0F0F5'
    }}>
      {/* Header */}
      <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid #F2F2F7' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:6 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
              <User size={14} color="#28419a" />
              <span style={{ fontWeight:700, fontSize:15, color:'#1C1C1E' }}>{lead.full_name || '—'}</span>
            </div>
            {lead.company && (
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                <Building2 size={12} color="#8E8E93" />
                <span style={{ fontSize:13, color:'#636366' }}>{lead.company}</span>
              </div>
            )}
            <div style={{ fontSize:12, color:'#AEAEB2' }}>{lead.email}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <Calendar size={12} color="#8E8E93" />
              <span style={{ fontSize:12, color:'#8E8E93' }}>{fmtDate(lead.last_activity)}</span>
            </div>
          </div>
        </div>

        {/* Scenarios list */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:8 }}>
          {lead.scenario_count > 1 && (
            <span style={{ fontSize:12, color:'#8E8E93', marginRight:4 }}>
              {lead.scenario_count} simulazioni:
            </span>
          )}
          {(lead.scenarios || []).map((s, i) => (
            <ScenarioBadge key={i} type={s.scenario_type} />
          ))}
        </div>
      </div>

      {/* Email draft */}
      <div style={{ padding:'14px 20px 18px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
          <Mail size={13} color="#8E8E93" />
          <span style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.4px', color:'#AEAEB2' }}>
            Bozza follow-up
          </span>
        </div>

        {generating && (
          <div style={{ display:'flex', alignItems:'center', gap:8, color:'#8E8E93', fontSize:13, padding:'10px 0' }}>
            <Loader size={14} style={{ animation:'spin 0.8s linear infinite' }} />
            Claude sta scrivendo…
          </div>
        )}

        {genError && !generating && (
          <div style={{ color:'#C0392B', fontSize:13, marginBottom:8 }}>
            ⚠️ {genError}
            <button onClick={generateEmail} style={{ marginLeft:10, fontSize:12, color:'#28419a', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
              Riprova
            </button>
          </div>
        )}

        {draft !== null && !generating && (
          <>
            {/* Subject field */}
            <div style={{ marginBottom:8 }}>
              <label style={{ fontSize:11, fontWeight:600, color:'#AEAEB2', textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:4 }}>
                Oggetto
              </label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Oggetto email…"
                style={{
                  width:'100%', borderRadius:8, border:'1px solid #E5E5EA',
                  background:'#F9F9FB', padding:'9px 12px', fontSize:13.5,
                  fontFamily:'inherit', color:'#2C2C2E', outline:'none', boxSizing:'border-box'
                }}
              />
            </div>
            <textarea
              id={`ta-${lead.user_id}`}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={12}
              style={{
                width:'100%', borderRadius:8, border:'1px solid #E5E5EA',
                background:'#F9F9FB', padding:'12px', fontSize:13.5, lineHeight:1.75,
                fontFamily:'inherit', color:'#2C2C2E', resize:'vertical',
                outline:'none', boxSizing:'border-box'
              }}
            />
            <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
              <button
                onClick={handleCopy}
                style={{
                  display:'flex', alignItems:'center', gap:6, padding:'9px 14px',
                  borderRadius:8, border:'1.5px solid #D1D1D6', background:'#fff',
                  fontSize:13, fontWeight:600, cursor:'pointer', color:'#1C1C1E'
                }}
              >
                {copied ? <Check size={14} color="#248A3D" /> : <Copy size={14} />}
                {copied ? 'Copiata!' : 'Copia email'}
              </button>
              <button
                onClick={handleOpenOutlook}
                disabled={!draft}
                style={{
                  display:'flex', alignItems:'center', gap:6, padding:'9px 14px',
                  borderRadius:8, border:'1.5px solid #248A3D', background:'#fff',
                  fontSize:13, fontWeight:600, cursor:'pointer', color:'#248A3D'
                }}
              >
                <ExternalLink size={14} />
                Apri in Outlook
              </button>
              <button
                onClick={generateEmail}
                style={{
                  display:'flex', alignItems:'center', gap:6, padding:'9px 14px',
                  borderRadius:8, border:'1.5px solid #28419a', background:'#fff',
                  fontSize:13, fontWeight:600, cursor:'pointer', color:'#28419a'
                }}
              >
                <RefreshCw size={14} />
                Rigenera
              </button>
              <button
                onClick={() => handleTranslate('it')}
                disabled={translating || currentLang === 'it'}
                style={{
                  padding:'9px 12px', borderRadius:8,
                  border:`1.5px solid ${currentLang === 'it' ? '#ccc' : '#8E8E93'}`,
                  background: currentLang === 'it' ? '#F5F5F5' : '#fff',
                  fontSize:13, fontWeight:600, cursor: (translating || currentLang === 'it') ? 'default' : 'pointer',
                  color: currentLang === 'it' ? '#aaa' : '#1C1C1E'
                }}
              >🇮🇹 IT</button>
              <button
                onClick={() => handleTranslate('en')}
                disabled={translating || currentLang === 'en'}
                style={{
                  padding:'9px 12px', borderRadius:8,
                  border:`1.5px solid ${currentLang === 'en' ? '#ccc' : '#8E8E93'}`,
                  background: currentLang === 'en' ? '#F5F5F5' : '#fff',
                  fontSize:13, fontWeight:600, cursor: (translating || currentLang === 'en') ? 'default' : 'pointer',
                  color: currentLang === 'en' ? '#aaa' : '#1C1C1E'
                }}
              >🇬🇧 EN</button>
              {translating && <span style={{ fontSize:12, color:'#8E8E93', alignSelf:'center' }}>Traduzione…</span>}
            </div>
          </>
        )}

        {sendError && (
          <div style={{ background:'#FFF2F2', border:'1px solid #FFCDD2', borderRadius:8, padding:'10px 14px', color:'#B71C1C', fontSize:13, marginTop:10 }}>
            ⚠️ {sendError}
          </div>
        )}

        {(draft !== null || genError) && (
          <button
            onClick={handleDone}
            disabled={marking || !draft}
            style={{
              marginTop:14, width:'100%', display:'flex', alignItems:'center', justifyContent:'center',
              gap:8, padding:'12px 16px', borderRadius:8, border:'none',
              background: (marking || !draft) ? '#ccc' : '#248A3D', color:'#fff',
              fontSize:14, fontWeight:700, cursor: (marking || !draft) ? 'not-allowed' : 'pointer'
            }}
          >
            <Mail size={15} />
            {marking ? 'Salvando…' : '✅ Segna come inviato e rimuovi dalla lista'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function FollowupPage() {
  const { token } = useApp();
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    axios.get(`${API}/followups`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setLeads(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.error || e.message); setLoading(false); });
  }, [token]);

  const handleDone = (userId) => setLeads(prev => prev.filter(l => l.user_id !== userId));

  if (loading) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'60vh', gap:14, color:'#8E8E93' }}>
      <Loader size={32} style={{ animation:'spin 0.8s linear infinite' }} />
      <span style={{ fontSize:14 }}>Caricamento lead…</span>
    </div>
  );

  if (error) return (
    <div style={{ padding:24, maxWidth:700, margin:'0 auto' }}>
      <div style={{ background:'#FFF2F2', border:'1px solid #FFCDD2', borderRadius:10, padding:'14px 18px', color:'#B71C1C', fontSize:14 }}>
        ⚠️ Errore: {error}
      </div>
    </div>
  );

  return (
    <div style={{ padding:'24px 20px 60px', maxWidth:760, margin:'0 auto' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <Mail size={22} color="#28419a" />
        <h1 style={{ fontSize:22, fontWeight:800, color:'#1C1C1E', margin:0 }}>Follow-up</h1>
        {leads.length > 0 && (
          <span style={{
            background:'#E65100', color:'#fff', borderRadius:20,
            padding:'3px 11px', fontSize:12, fontWeight:700
          }}>
            {leads.length}
          </span>
        )}
      </div>

      {leads.length === 0 ? (
        <div style={{ textAlign:'center', padding:'80px 20px', color:'#8E8E93' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🎉</div>
          <div style={{ fontSize:16, fontWeight:600 }}>Nessun follow-up pendente!</div>
          <div style={{ fontSize:13, marginTop:6 }}>Tutti i lead sono stati gestiti.</div>
        </div>
      ) : (
        leads.map(lead => (
          <LeadCard key={lead.user_id} lead={lead} onDone={handleDone} token={token} />
        ))
      )}
    </div>
  );
}
